import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, LinearGradient, isWeb } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import type { ImageThumbnailProps, ImageThumbnailRatio } from "./types";

const ratioMap: Record<Exclude<ImageThumbnailRatio, "custom">, number> = {
  "1:1": 1,
  "16:9": 16 / 9,
  "4:3": 4 / 3,
  "3:2": 3 / 2,
  "2:3": 2 / 3,
  "9:16": 9 / 16,
};

export const ImageThumbnail: React.FC<ImageThumbnailProps> = ({
  src,
  alt = "",
  ratio = "16:9",
  customRatio,
  width = "100%",
  height,
  borderRadius = 4,
  overlay = "fade",
  centerContent,
  tagsTopLeft,
  tagsTopRight,
  tagsBottomLeft,
  tagsBottomRight,
  onPress,
  tagPadding = 8,
  tagGap = 4,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  const aspectRatio = ratio === "custom" ? customRatio : ratioMap[ratio];
  const wrapperProps = onPress ? { onPress } : {};

  const containerStyle = isWeb
    ? {
        aspectRatio: height ? undefined : aspectRatio,
        borderRadius,
        overflow: "hidden" as const,
      }
    : {
        aspectRatio: height ? undefined : aspectRatio,
        borderRadius,
        overflow: "hidden" as const,
      };

  return (
    <Box
      testID={testID}
      width={width}
      height={height}
      position="relative"
      overflow="hidden"
      borderRadius={borderRadius}
      style={containerStyle}
      {...wrapperProps}
    >
      {/* Background Image */}
      <Box
        as="img"
        src={src}
        alt={alt}
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        style={isWeb ? { objectFit: "cover" } : undefined}
      />

      {/* Overlay */}
      {overlay === "fade" && (
        <LinearGradient
          colors={["transparent", "rgba(0, 0, 0, 0.48)"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
        />
      )}
      {overlay === "dark" && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor="rgba(0, 0, 0, 0.48)"
        />
      )}

      {/* Center Content (e.g., play button) */}
      {centerContent && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          alignItems="center"
          justifyContent="center"
          zIndex={10}
        >
          {centerContent}
        </Box>
      )}

      {/* Top Left Tags */}
      {tagsTopLeft && (
        <Box
          position="absolute"
          top={tagPadding}
          left={tagPadding}
          flexDirection="column"
          gap={tagGap}
          alignItems="flex-start"
          zIndex={10}
        >
          {tagsTopLeft}
        </Box>
      )}

      {/* Top Right Tags */}
      {tagsTopRight && (
        <Box
          position="absolute"
          top={tagPadding}
          right={tagPadding}
          flexDirection="column"
          gap={tagGap}
          alignItems="flex-end"
          zIndex={10}
        >
          {tagsTopRight}
        </Box>
      )}

      {/* Bottom Left Tags */}
      {tagsBottomLeft && (
        <Box
          position="absolute"
          bottom={tagPadding}
          left={tagPadding}
          flexDirection="column"
          gap={tagGap}
          alignItems="flex-start"
          zIndex={10}
        >
          {tagsBottomLeft}
        </Box>
      )}

      {/* Bottom Right Tags */}
      {tagsBottomRight && (
        <Box
          position="absolute"
          bottom={tagPadding}
          right={tagPadding}
          flexDirection="column"
          gap={tagGap}
          alignItems="flex-end"
          zIndex={10}
        >
          {tagsBottomRight}
        </Box>
      )}
    </Box>
  );
};
